import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NOTIFICATION_EVENT_CATALOG } from '@lite-workspace/dto-notification';
import { HlmCard } from '@lite-workspace/shared/helm-ui/card';
import { HlmIconComponent } from '@lite-workspace/shared/helm-ui/icon';
import { HlmSwitch } from '@lite-workspace/shared/helm-ui/switch';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { IdentityStore } from '@lite-workspace/shared/identity';
import { NotificationPreferencesComponent } from './notification-preferences.component';

// The in-app feed keeps receiving events regardless of the email switches.
// Hosts set the inputs through route `data`: a cabinet without the feed sends «back» to its account settings.
@Component({
  selector: 'app-notification-settings-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmCard, HlmIconComponent, HlmSwitch, NotificationPreferencesComponent, RouterLink],
  template: `
    <div class="mx-auto w-full max-w-[720px] px-5 pb-16 pt-8">
      <div class="flex items-center gap-3">
        <a
          [routerLink]="back()"
          [attr.aria-label]="backAria()"
          class="grid size-8 place-items-center rounded-full border border-line2 bg-panel3 text-c2 hover:text-ink"
        >
          <hlm-icon src="/assets/icons/chevron-left.svg" [size]="15" />
        </a>
        <div>
          <h1 class="text-xl font-semibold text-ink">Настройки уведомлений</h1>
          <p class="mt-0.5 text-sm text-c3">Какие письма присылать. Лента в приложении получает все события.</p>
        </div>
      </div>

      <div class="mt-6 flex flex-col gap-6">
        <app-notification-preferences [eventTypes]="eventTypes()" />
        <!-- Unknown until the profile arrives: a switch drawn before that would show a guess. -->
        @if (showMarketing() && marketingUnsubscribed() !== undefined) {
          <div hlmCard class="block gap-0 p-6 md:p-7">
            <div class="flex items-start justify-between gap-4">
              <span class="text-sm text-ink">Получать рассылки</span>
              <hlm-switch
                [checked]="!marketingUnsubscribed()"
                (checkedChange)="onMarketingToggle($event)"
                aria-label="Получать рассылки"
              />
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class NotificationSettingsPageComponent implements OnInit {
  private readonly identity = inject(IdentityStore);
  private readonly toast = inject(ToastService);

  // Route-data inputs: the router writes `undefined` for a key the route lacks, so defaults live below.
  /** One product's mails only (inbox product tag); unset = the whole catalog. */
  readonly product = input<string>();
  readonly backLink = input<string>();
  readonly backLabel = input<string>();
  /** `false` hides «Получать рассылки» where the user is not a photographer (admin). */
  readonly marketing = input<boolean>();

  protected readonly back = computed(() => this.backLink() ?? '/notifications');
  protected readonly backAria = computed(() => this.backLabel() ?? 'К уведомлениям');
  protected readonly showMarketing = computed(() => this.marketing() !== false);

  protected readonly eventTypes = computed(() => {
    const product = this.product();
    if (!product) return null;
    return NOTIFICATION_EVENT_CATALOG.find(c => c.product === product)?.events.map(e => e.eventType) ?? [];
  });

  protected readonly marketingUnsubscribed = computed(() => this.identity.profile()?.marketingUnsubscribed);

  ngOnInit(): void {
    if (this.showMarketing()) this.identity.ensureProfile();
  }

  protected async onMarketingToggle(subscribed: boolean): Promise<void> {
    if (await this.identity.setMarketingUnsubscribed(!subscribed)) this.toast.success('Изменения сохранены');
    else this.toast.error('Не удалось сохранить изменения');
  }
}
